import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { SensitiveData } from '@/components/sensitive-data';
import { KPI } from '@/components/ui/kpi';
import { CompactKpiCard } from '@/components/kpi-card';
import { Card, CardContent } from '@/components/ui/card';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { BackToProjectLink } from '@/components/BackToReportNav';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import type { ColumnDef, ColumnSort } from '@/components/ui/data-table-enhanced';
import {
  TrendingUp,
  TrendingDown,
  Search,
  X,
  AlertTriangle,
  ExternalLink,
  Info,
  Clock,
  DollarSign,
  Wallet,
  TriangleAlert,
  Download,
  Loader2,
  Check,
  ChevronsUpDown,
  Banknote,
  ArrowLeft,
  Receipt,
} from 'lucide-react';
import { fetchProjectForecastDataPaged, fetchProjects } from '@/lib/data-service';
import type { ForecastReportParams } from '@/lib/data-service';
import { getBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage';
import { calculateEAC, formatCurrency, formatDate, healthToVariant, cn } from '@/lib/utils';
import type { EacMethod } from '@/lib/utils';
import { useEacSettings } from '@/contexts/EacSettingsContext';
import type { ProjectForecastData, ForecastSummary, ForecastFilterOptions } from '@/types/reports.types';
import type { PaginationMetadata } from '@/types/pagination';
import { ROUTES } from '@/routes';
import { PaginationControls } from '@/components/pagination-controls';
import { DEFAULT_PAGE_SIZE } from '@/types/pagination';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { exportForecastToExcel } from '@/lib/export-forecast-excel';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Cash Flow mock data
// ─────────────────────────────────────────────────────────────────────────────



const SUPPLIER_PAYMENT_TERMS_DAYS = 30;
const CLIENT_PAYMENT_TERMS_DAYS = 30;

const mockCashFlowMonths = [
  { month: '2026-01', forecastCosts: 18000, forecastRevenue: 0 },
  { month: '2026-02', forecastCosts: 22000, forecastRevenue: 0 },
  { month: '2026-03', forecastCosts: 25000, forecastRevenue: 15000 },
  { month: '2026-04', forecastCosts: 28000, forecastRevenue: 22000 },
  { month: '2026-05', forecastCosts: 24000, forecastRevenue: 28000 },
  { month: '2026-06', forecastCosts: 20000, forecastRevenue: 32000 },
  { month: '2026-07', forecastCosts: 18000, forecastRevenue: 35000 },
  { month: '2026-08', forecastCosts: 15000, forecastRevenue: 30000 },
];

interface CashFlowRow {
  month: string;
  forecastCosts: number;
  cashOut: number;
  forecastRevenue: number;
  cashIn: number;
  netCashFlow: number;
  cumulativeCashFlow: number;
}

function buildCashFlowRows(months: typeof mockCashFlowMonths): CashFlowRow[] {
  let cumulative = 0;
  return months.map((m, i) => {
    // Shift costs and revenue forward by 1 month (30-day terms)
    const cashOut = i > 0 ? months[i - 1].forecastCosts : 0;
    const cashIn = i > 0 ? months[i - 1].forecastRevenue : 0;
    const net = cashIn - cashOut;
    cumulative += net;
    return {
      month: m.month,
      forecastCosts: m.forecastCosts,
      cashOut,
      forecastRevenue: m.forecastRevenue,
      cashIn,
      netCashFlow: net,
      cumulativeCashFlow: cumulative,
    };
  });
}

function formatMonthLabel(monthStr: string): string {
  const [y, m] = monthStr.split('-').map(Number);
  return new Date(y, m - 1).toLocaleString('en-GB', { month: 'short', year: '2-digit' });
}

function formatMonthFull(monthStr: string): string {
  const [y, m] = monthStr.split('-').map(Number);
  return new Date(y, m - 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
}

function isPastMonth(monthStr: string): boolean {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  const today = new Date();
  return d < new Date(today.getFullYear(), today.getMonth(), 1);
}

// ─────────────────────────────────────────────────────────────────────────────

function getDelayDays(project: ProjectForecastData): number | null {
  if (!project.forecastEndDate || !project.endDate) return null;
  const forecast = new Date(project.forecastEndDate).getTime();
  const planned = new Date(project.endDate).getTime();
  const diff = Math.round((forecast - planned) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
}

/** Variance colour: positive = overrun (bad) = red, negative = under budget (good) = green */
function VariancePill({ value, isPercent = false }: { value: number; isPercent?: boolean }) {
  const absVal = Math.abs(value);
  const threshold = isPercent ? 2 : 0;

  if (absVal <= threshold) {
    return <span className="text-muted-foreground text-sm">≈ On plan</span>;
  }

  const label = isPercent
    ? `${value > 0 ? '+' : '−'}${absVal.toFixed(1)}%`
    : `${value > 0 ? '+' : '−'}${formatCurrency(absVal)}`;

  const colour = value > 0
    ? 'text-error-600 dark:text-error-400'
    : 'text-success-600 dark:text-success-400';

  return (
    <span className={`text-sm font-medium ${colour}`}>
      {value > 0 ? <TrendingUp className="inline h-3 w-3 mr-0.5" /> : <TrendingDown className="inline h-3 w-3 mr-0.5" />}
      {label}
    </span>
  );
}

function HealthBadge({ health, label }: { health: 'green' | 'amber' | 'red'; label?: string }) {
  const variant = healthToVariant(health);
  const defaultLabel = health === 'green' ? 'On Track' : health === 'amber' ? 'At Risk' : 'Off Track';
  return (
    <Badge variant={variant} className="text-xs gap-1.5">
      <StatusIndicator variant={variant === 'destructive' ? 'error' : variant} size="sm" />
      {label ?? defaultLabel}
    </Badge>
  );
}

function CpiLabel({ cpi }: { cpi: number }) {
  if (cpi >= 1.0) {
    return <span className="text-success-600 text-sm">CPI: {cpi.toFixed(2)} 🟢 Performing ahead of plan</span>;
  }
  if (cpi >= 0.9) {
    return <span className="text-warning-600 text-sm">CPI: {cpi.toFixed(2)} 🟡 Slightly behind plan</span>;
  }
  return <span className="text-error-600 text-sm">CPI: {cpi.toFixed(2)} 🔴 Significantly behind plan</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Enriched row type
// ─────────────────────────────────────────────────────────────────────────────

interface ForecastRow {
  project: ProjectForecastData;
  totalBudget: number;
  eac: number;
  forecastVariance: number;
  forecastVariancePct: number;
  eacMarginPct: number | null;
  forecastRevenue: number;
  forecastMargin: number;
  forecastMarginPct: number | null;
  health: 'green' | 'amber' | 'red';
  pmName: string;
  delayDays: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Side Sheet
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Single-Project Cash Flow View
// ─────────────────────────────────────────────────────────────────────────────

function SingleProjectCashFlowContent({ projectId, projectName, projectCode }: { projectId: number; projectName?: string; projectCode?: string }) {
  const project = projectName ? { name: projectName, code: projectCode ?? '' } : null;
  const rows = useMemo(() => buildCashFlowRows(mockCashFlowMonths), []);

  // Summary cards
  const peakCashReq = useMemo(() => {
    const min = Math.min(...rows.map(r => r.cumulativeCashFlow));
    const row = rows.find(r => r.cumulativeCashFlow === min);
    return { value: min, month: row?.month ?? '' };
  }, [rows]);

  const breakEvenMonth = useMemo(
    () => rows.find(r => r.cumulativeCashFlow > 0)?.month ?? null,
    [rows]
  );

  const totalNetCashFlow = useMemo(
    () => rows.reduce((s, r) => s + r.netCashFlow, 0),
    [rows]
  );

  const chartData = useMemo(
    () => rows.map(r => ({ month: formatMonthLabel(r.month), cumulative: r.cumulativeCashFlow })),
    [rows]
  );

  type RowKey = 'forecastCosts' | 'cashOut' | 'forecastRevenue' | 'cashIn' | 'netCashFlow' | 'cumulativeCashFlow';
  const rowDefs: { key: RowKey; label: string; sublabel?: string; bold?: boolean; highlight?: boolean; muted?: boolean; colorCoded?: boolean }[] = [
    { key: 'forecastCosts',      label: 'Forecast Costs',      muted: true },
    { key: 'cashOut',            label: 'Cash Out',            sublabel: `(${SUPPLIER_PAYMENT_TERMS_DAYS}-day supplier terms)`, colorCoded: false },
    { key: 'forecastRevenue',    label: 'Forecast Revenue',    muted: true },
    { key: 'cashIn',             label: 'Cash In',             sublabel: `(${CLIENT_PAYMENT_TERMS_DAYS}-day client terms)`, colorCoded: false },
    { key: 'netCashFlow',        label: 'Net Cash Flow',       colorCoded: true },
    { key: 'cumulativeCashFlow', label: 'Cumulative Cash Flow', bold: true, highlight: true, colorCoded: true },
  ];

  return (
    <div className="px-6 pb-6 space-y-5">
      {/* Project banner */}
      {project && (
        <div className="flex items-center gap-2 pt-2 pb-1">
          <Banknote className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{project.name}</span>
          <Badge variant="outline" className="text-xs font-mono">{project.code}</Badge>
          <span className="text-xs text-muted-foreground ml-1">— Cash Flow View</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <CompactKpiCard
          title="Peak Cash Requirement"
          value={<SensitiveData><span className={peakCashReq.value < 0 ? 'text-error-600' : 'text-success-600'}>{formatCurrency(Math.abs(peakCashReq.value))}</span></SensitiveData>}
          description={peakCashReq.month ? `Lowest point: ${formatMonthFull(peakCashReq.month)}` : '—'}
          icon={TrendingDown}
          accentVariant={peakCashReq.value < 0 ? 'error' : 'success'}
          className="shadow-none"
        />
        <CompactKpiCard
          title="Break-Even Month"
          value={breakEvenMonth ? formatMonthFull(breakEvenMonth) : 'Not reached'}
          description="First month cumulative cash flow turns positive"
          icon={TrendingUp}
          accentVariant={breakEvenMonth ? 'success' : 'warning'}
          className="shadow-none"
        />
        <CompactKpiCard
          title="Total Net Cash Flow"
          value={<SensitiveData><span className={totalNetCashFlow >= 0 ? 'text-success-600' : 'text-error-600'}>{totalNetCashFlow >= 0 ? '+' : ''}{formatCurrency(totalNetCashFlow)}</span></SensitiveData>}
          description="Sum of all monthly net cash flows"
          icon={totalNetCashFlow >= 0 ? TrendingUp : TrendingDown}
          accentVariant={totalNetCashFlow >= 0 ? 'success' : 'error'}
          className="shadow-none"
        />
      </div>

      {/* Cumulative cash flow chart */}
      <div className="rounded-lg border bg-card p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Cumulative Cash Flow</p>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={v => `£${Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} tick={{ fontSize: 11 }} width={56} />
            <RechartsTooltip
              formatter={(value: number) => [formatCurrency(value), 'Cumulative Cash Flow']}
              labelStyle={{ fontWeight: 600 }}
            />
            <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="4 4" label={{ value: '£0', fontSize: 10, fill: '#6b7280', position: 'right' }} />
            <Line
              type="monotone"
              dataKey="cumulative"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, value } = props;
                const color = (value as number) >= 0 ? '#16a34a' : '#dc2626';
                return <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={4} fill={color} stroke="white" strokeWidth={1.5} />;
              }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Payment terms bar */}
      <div className="flex justify-end">
        <p className="text-xs text-muted-foreground">
          Supplier terms: <span className="font-medium">{SUPPLIER_PAYMENT_TERMS_DAYS} days</span>
          <span className="mx-2">·</span>
          Client terms: <span className="font-medium">{CLIENT_PAYMENT_TERMS_DAYS} days</span>
        </p>
      </div>

      {/* Monthly cash flow grid */}
      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-muted/40 border-b">
                <th className="sticky left-0 z-10 bg-muted/40 text-left px-3 py-2 font-semibold text-muted-foreground min-w-[180px]">Row</th>
                {rows.map(r => (
                  <th
                    key={r.month}
                    className={cn(
                      'px-3 py-2 text-right font-medium whitespace-nowrap min-w-[80px]',
                      isPastMonth(r.month) ? 'text-muted-foreground/50' : 'text-muted-foreground'
                    )}
                  >
                    {formatMonthLabel(r.month)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowDefs.map((def, rowIdx) => (
                <tr
                  key={def.key}
                  className={cn(
                    'border-b last:border-0',
                    def.highlight ? 'bg-primary/5' : rowIdx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  )}
                >
                  <td className={cn(
                    'sticky left-0 z-10 px-3 py-2 font-medium whitespace-nowrap border-r',
                    def.highlight ? 'bg-primary/5' : rowIdx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  )}>
                    <div className="flex flex-col">
                      <span className={cn(def.bold ? 'font-semibold' : 'font-medium', def.muted ? 'text-muted-foreground' : 'text-foreground')}>
                        {def.label}
                      </span>
                      {def.sublabel && (
                        <span className="text-[10px] text-muted-foreground">{def.sublabel}</span>
                      )}
                    </div>
                  </td>
                  {rows.map(r => {
                    const raw = r[def.key] as number;
                    const past = isPastMonth(r.month);
                    let valueClass = cn(
                      def.bold ? 'font-semibold' : '',
                      past ? 'opacity-40' : '',
                      def.colorCoded
                        ? raw > 0 ? 'text-success-600' : raw < 0 ? 'text-error-600' : 'text-muted-foreground'
                        : def.muted ? 'text-muted-foreground' : 'text-foreground'
                    );
                    return (
                      <td key={r.month} className={cn('px-3 py-2 text-right tabular-nums', past ? 'bg-muted/10' : '')}>
                        <SensitiveData>
                          <span className={valueClass}>{formatCurrency(raw)}</span>
                        </SensitiveData>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface SideSheetProps {
  row: ForecastRow;
  onClose: () => void;
}

function eacMethodLabel(method: EacMethod): string {
  if (method === 'simple') return 'Simple';
  if (method === 'performance-based') return 'Performance-Based (CPI)';
  return 'Re-estimate';
}

function ForecastSideSheet({ row, onClose }: SideSheetProps) {
  const navigate = useNavigate();
  const { project, totalBudget } = row;

  // EAC method comes from org settings — no local override allowed
  const { eacMethod } = useEacSettings();

  const completionPercent = project.completionPercent ?? 0;
  const actualCosts = project.forecastInputs.actualCosts;

  // ETC from forecast tab state — not accessible from the report; falls back to
  // Simple inside calculateEAC when estimateToComplete is 0.
  const etc = 0;

  const eacResult = useMemo(
    () =>
      totalBudget > 0
        ? calculateEAC(
            { actualCosts, totalBudget, completionPercent, estimateToComplete: etc },
            eacMethod
          )
        : null,
    [actualCosts, totalBudget, completionPercent, eacMethod, etc]
  );

  // calculateEAC falls back to Simple when Re-estimate has no ETC, so report the
  // method actually applied rather than the org setting.
  const appliedMethod = eacResult?.method ?? eacMethod;
  const reEstimateFellBack = eacMethod === 're-estimate' && appliedMethod !== 're-estimate';

  const projectedFinalRevenue = project.revenueData.projectedFinalRevenue;

  const budgetRevenue = project.revenueData.budgetRevenue;

  // Margin calculations
  const budgetMarginPct = budgetRevenue > 0 ? ((budgetRevenue - totalBudget) / budgetRevenue) * 100 : null;
  const forecastMarginPct =
    projectedFinalRevenue && projectedFinalRevenue > 0 && eacResult
      ? ((projectedFinalRevenue - eacResult.eac) / projectedFinalRevenue) * 100
      : null;

  // Hours
  const budgetHours = project.hoursData.budgetHours;
  const actualHours = project.hoursData.actualHours;
  // Linear projection for EAC hours
  const eacHours =
    completionPercent > 0
      ? Math.round((actualHours / completionPercent) * 100)
      : budgetHours;

  return (
    <Sheet open onOpenChange={() => onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span>{project.projectName}</span>
            {project.projectCode && (
              <Badge variant="outline" className="text-xs font-mono">
                {project.projectCode}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription asChild>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div><span className="font-medium text-foreground">Client:</span> {project.clientName}</div>
              <div><span className="font-medium text-foreground">PM:</span> {row.pmName}</div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">Status:</span>
                <Badge variant="outline" className="text-xs">{project.status}</Badge>
                <HealthBadge health={row.health} />
              </div>
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 py-4 space-y-6">

          {/* EAC Method — read-only, driven by org settings */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">EAC Method</span>
              <span className="font-medium">{eacMethodLabel(appliedMethod)}</span>
            </div>
            {reEstimateFellBack && (
              <p className="text-xs text-muted-foreground italic flex items-center gap-1">
                <Info className="h-3 w-3" />
                Re-estimate needs an Estimate to Complete from the project's Forecast tab; the Simple method is shown instead.
              </p>
            )}
          </div>

          {/* Cost Forecast */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Cost Forecast</h3>
            {totalBudget === 0 ? (
              <p className="text-sm text-muted-foreground">No cost budget has been configured for this project.</p>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Budget Cost</span>
                  <SensitiveData><span className="font-medium">{formatCurrency(totalBudget)}</span></SensitiveData>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Actual Cost</span>
                  <SensitiveData><span className="font-medium">{formatCurrency(actualCosts)}</span></SensitiveData>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completion</span>
                  <span className="font-medium">{completionPercent}%</span>
                </div>
                {completionPercent === 0 && (
                  <p className="text-xs text-muted-foreground italic flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    EAC equals full budget when completion is 0%.
                  </p>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="text-muted-foreground font-medium">EAC Cost</span>
                  {eacResult ? (
                    <SensitiveData>
                      <span className="font-semibold">{formatCurrency(eacResult.eac)}</span>
                    </SensitiveData>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                {eacResult && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Variance vs Budget</span>
                    <SensitiveData>
                      <VariancePill value={eacResult.costVarianceAtCompletion} />
                    </SensitiveData>
                  </div>
                )}
                {appliedMethod === 'performance-based' && eacResult?.cpi != null && (
                  <div className="pt-1">
                    <CpiLabel cpi={eacResult.cpi} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Revenue Forecast */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Revenue Forecast</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Forecast Revenue</span>
                {projectedFinalRevenue != null ? (
                  <SensitiveData><span className="font-semibold">{formatCurrency(projectedFinalRevenue)}</span></SensitiveData>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Contract Value</span>
                <SensitiveData><span className="font-medium">{formatCurrency(budgetRevenue)}</span></SensitiveData>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actual Revenue</span>
                <SensitiveData><span className="font-medium">{formatCurrency(project.revenueData.actualSales)}</span></SensitiveData>
              </div>
              {budgetRevenue > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">% Cost Complete</span>
                  <span className="font-medium">{completionPercent}%</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground font-medium">Revenue Recognition</span>
                  <span className="text-xs text-muted-foreground">% Cost Complete</span>
                </div>
                {projectedFinalRevenue != null && budgetRevenue > 0 ? (
                  <SensitiveData>
                    <span className="font-semibold">{formatCurrency((completionPercent / 100) * budgetRevenue)}</span>
                  </SensitiveData>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
            </div>
          </div>

          {/* Margin Forecast */}
          {(budgetMarginPct != null || forecastMarginPct != null) && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Margin Forecast</h3>
              <div className="space-y-2 text-sm">
                {budgetMarginPct != null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Budget Margin</span>
                    <SensitiveData><span className="font-medium">{budgetMarginPct.toFixed(1)}%</span></SensitiveData>
                  </div>
                )}
                {forecastMarginPct != null && (
                  <>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-muted-foreground font-medium">Forecast Margin</span>
                      <SensitiveData><span className="font-semibold">{forecastMarginPct.toFixed(1)}%</span></SensitiveData>
                    </div>
                    {budgetMarginPct != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">vs Budget</span>
                        <SensitiveData>
                          <VariancePill value={-(forecastMarginPct - budgetMarginPct)} isPercent />
                        </SensitiveData>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Hours Forecast */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Hours Forecast</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Budget Hours</span>
                <span className="font-medium">{budgetHours.toLocaleString()} hrs</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actual Hours</span>
                <span className="font-medium">{actualHours.toLocaleString()} hrs</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-muted-foreground font-medium">EAC Hours</span>
                <span className="font-semibold">{eacHours.toLocaleString()} hrs</span>
              </div>
              {budgetHours > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Variance</span>
                  <VariancePill value={eacHours - budgetHours} />
                </div>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">Schedule</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Planned End</span>
                <span className="font-medium">{project.endDate ? formatDate(project.endDate) : '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Forecast End</span>
                {project.forecastEndDate ? (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatDate(project.forecastEndDate)}</span>
                    {row.delayDays && (
                      <span className="text-warning-600 text-xs flex items-center gap-0.5">
                        <Clock className="h-3 w-3" />
                        {row.delayDays} days late
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-muted-foreground">Not set</span>
                    <button
                      onClick={() => navigate(ROUTES.PROJECT_DETAILS(project.projectId))}
                      className="text-xs text-primary hover:underline flex items-center gap-0.5"
                    >
                      Update on project record <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer links */}
          <div className="border-t pt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`${ROUTES.BUDGET_VARIANCE_REPORT}?reportProjectId=${project.projectId}`)}
            >
              View Budget Variance →
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.PROJECT_DETAILS(project.projectId))}
            >
              Open Project →
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export function ProjectForecastReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read ?managerId= from URL (passed by homepage PM quick link)
  const initManagerId = searchParams.get('managerId') ? Number(searchParams.get('managerId')) : null;
  const initReportProjectId = searchParams.get('reportProjectId');

  // Project filter — drives single-project cash flow view when set
  const initProjectId = searchParams.get('projectId') ? Number(searchParams.get('projectId')) : null;
  const [projectFilter, setProjectFilter] = useState<number | null>(initProjectId);

  const handleProjectFilterChange = useCallback((id: number | null) => {
    setProjectFilter(id);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id) {
        next.set('projectId', String(id));
      } else {
        next.delete('projectId');
      }
      return next;
    });
  }, [setSearchParams]);

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectForecastData[]>([]);
  const [serverSummary, setServerSummary] = useState<ForecastSummary | null>(null);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [filterOptions, setFilterOptions] = useState<ForecastFilterOptions>({ projectManagers: [], statuses: [] });
  const { handleApiError } = useErrorHandler({ useToast: true });

  // Filter state — initialise search from deep-link project code
  const [searchTerm, setSearchTerm] = useState('');
  const [pmFilter, setPmFilter] = useState<string>('all');
  const [pmComboboxOpen, setPmComboboxOpen] = useState(false);
  const [billableFilter, setBillableFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>(initReportProjectId ? 'all' : 'active');

  // Deep-link: fetch the project to get its code for the search filter
  useEffect(() => {
    if (initReportProjectId) {
      import('@/lib/data-service').then(({ fetchProjectById }) => {
        fetchProjectById(Number(initReportProjectId)).then((p) => {
          if (p) {
            const code = (p as any).code || (p as any).name || '';
            setSearchTerm(code);
          }
        }).catch(() => { /* ignore */ });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EAC method — driven by org settings (Settings → General → Reporting)
  const { eacMethod: globalMethod } = useEacSettings();

  // Sort state — default: project name ascending (server-side sort keys)
  const [sortKey, setSortKey] = useState<string>('project');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Column sort state for the DataTableEnhanced
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isExporting, setIsExporting] = useState(false);

  // Side sheet
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  const thresholds = useMemo(() => getBudgetHealthThresholds(), []);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm]);

  // Build fetch params — status "active" maps to statusIds 1,2 on the backend
  const fetchParams = useMemo((): ForecastReportParams => ({
    page: currentPage,
    pageSize,
    status: statusFilter === 'active' ? 'active' : statusFilter !== 'all' ? statusFilter : undefined,
    pm: pmFilter !== 'all' ? pmFilter : undefined,
    billable: billableFilter !== 'all' ? billableFilter : undefined,
    search: debouncedSearch || undefined,
    sortBy: sortKey,
    sortOrder: sortDir,
  }), [currentPage, pageSize, statusFilter, pmFilter, billableFilter, debouncedSearch, sortKey, sortDir]);

  // ── Load projects ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setFetchError(null);
      try {
        const result = await fetchProjectForecastDataPaged(fetchParams);
        if (cancelled) return;
        setProjects(result.projects);
        setServerSummary(result.summary);
        setPagination(result.pagination);
        setFilterOptions(result.filterOptions);
      } catch (error) {
        if (!cancelled) {
          handleApiError(error, '/api/reports/project-forecast', 'GET');
          setFetchError('Unable to load project forecast data. The report endpoint may not be available.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [fetchParams, handleApiError]);

  // Seed pmFilter from ?managerId= once project data is loaded.
  // Forecast rows use pmName (string) for filtering. We fetch projects filtered
  // by pmId to resolve the manager's name, then seed the filter dropdown.
  useEffect(() => {
    if (!initManagerId || loading || projects.length === 0) return;
    fetchProjects('projects', undefined, { pmId: initManagerId })
      .then(matchedProjects => {
        if (matchedProjects.length === 0) return;
        const matchedIds = new Set(matchedProjects.map((p: { id: number }) => p.id));
        const match = projects.find(p => matchedIds.has(p.projectId));
        if (match?.projectManager) setPmFilter(match.projectManager);
      })
      .catch(() => { /* silent — filter just won't be pre-seeded */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, projects]);

  // ── Enrich rows (EAC is still client-side) ─────────────────────────────────
  const enriched: ForecastRow[] = useMemo(() => {
    return projects.map(project => {
      const totalBudget = project.forecastInputs.totalBudget;
      const actualCosts = project.forecastInputs.actualCosts;
      const completionPercent = project.forecastInputs.completionPercent;

      const eacResult =
        totalBudget > 0 && completionPercent >= 0
          ? calculateEAC({ actualCosts, totalBudget, completionPercent }, globalMethod)
          : null;

      const eac = eacResult?.eac ?? totalBudget;
      const forecastVariance = eac - totalBudget;
      const forecastVariancePct = totalBudget > 0 ? (forecastVariance / totalBudget) * 100 : 0;

      const projectedFinalRevenue = project.revenueData.projectedFinalRevenue;
      const eacMarginPct =
        projectedFinalRevenue && projectedFinalRevenue > 0
          ? ((projectedFinalRevenue - eac) / projectedFinalRevenue) * 100
          : null;

      // Portfolio additions
      const forecastRevenue = projectedFinalRevenue ?? 0;
      const forecastMargin = forecastRevenue - eac;
      const forecastMarginPct = forecastRevenue > 0 ? (forecastMargin / forecastRevenue) * 100 : null;

      return {
        project,
        totalBudget,
        eac,
        forecastVariance,
        forecastVariancePct,
        eacMarginPct,
        forecastRevenue,
        forecastMargin,
        forecastMarginPct,
        health: project.health,
        pmName: project.projectManager || '—',
        delayDays: getDelayDays(project),
      };
    });
  }, [projects, globalMethod]);

  // ── PM options for filter ───────────────────────────────────────────────────
  const pmOptions = useMemo(() => {
    return filterOptions.projectManagers;
  }, [filterOptions]);

  // ── Reset page when filters change ─────────────────────────────────────────
  const prevFilterKey = useRef('');
  const filterKey = `${statusFilter}|${pmFilter}|${billableFilter}|${debouncedSearch}`;
  if (filterKey !== prevFilterKey.current) {
    prevFilterKey.current = filterKey;
    if (currentPage !== 1) setCurrentPage(1);
  }

  // ── Summary cards (EAC totals still computed client-side from server page) ─
  const summary = useMemo(() => {
    const totalEac = enriched.reduce((sum, r) => sum + r.eac, 0);
    const totalBudget = serverSummary?.totalBudgetCost ?? enriched.reduce((sum, r) => sum + r.totalBudget, 0);
    const forecastVariance = totalEac - totalBudget;
    const atRisk = serverSummary?.atRiskCount ?? enriched.filter(r => r.health === 'amber' || r.health === 'red').length;
    const totalProjects = serverSummary?.totalProjects ?? enriched.length;
    const totalForecastRevenue = enriched.reduce((sum, r) => sum + r.forecastRevenue, 0);
    const totalForecastMargin = enriched.reduce((sum, r) => sum + r.forecastMargin, 0);
    return { totalEac, totalBudget, forecastVariance, atRisk, totalProjects, totalForecastRevenue, totalForecastMargin };
  }, [enriched, serverSummary]);

  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // ── Handle column sort changes ─────────────────────────────────────────────
  const handleColumnSortChange = useCallback((sorts: ColumnSort[]) => {
    setColumnSorts(sorts);
    if (sorts.length > 0) {
      setSortKey(sorts[0].columnId);
      setSortDir(sorts[0].direction);
    }
    setCurrentPage(1);
  }, []);

  // ── Selected row for side sheet ────────────────────────────────────────────
  const selectedRow = useMemo(
    () => enriched.find(r => r.project.projectId === selectedProjectId) ?? null,
    [enriched, selectedProjectId]
  );

  // ── Row click handler ──────────────────────────────────────────────────────
  const handleRowClick = useCallback(
    (row: ForecastRow) => setSelectedProjectId(row.project.projectId),
    []
  );

  // ── Export to Excel ──────────────────────────────────────────────────────
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      const allResult = await fetchProjectForecastDataPaged({
        ...fetchParams,
        page: 1,
        pageSize: 50000,
      });
      await exportForecastToExcel(allResult.projects, globalMethod);
      toast.success(`Export complete — ${allResult.projects.length} projects downloaded.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, fetchParams, globalMethod]);

  // ── Column definitions — memoised so TanStack Table doesn't reinitialise ──
  const columns: ColumnDef<ForecastRow>[] = useMemo(() => [
    {
      id: 'health',
      header: 'Health',
      cell: ({ row }) => <HealthBadge health={row.original.health} />,
    },
    {
      id: 'project',
      header: 'Project',
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{row.original.project.projectName}</div>
          {row.original.project.projectCode && (
            <div className="text-xs text-muted-foreground font-mono">{row.original.project.projectCode}</div>
          )}
        </div>
      ),
    },
    {
      id: 'client',
      header: 'Client',
      cell: ({ row }) => <span className="text-sm">{row.original.project.clientName}</span>,
    },
    {
      id: 'pm',
      header: 'PM',
      cell: ({ row }) => <span className="text-sm">{row.original.pmName}</span>,
    },
    {
      id: 'billable',
      header: 'Billable',
      cell: ({ row }) => {
        const isBillable = row.original.project.billable ?? true;
        return isBillable
          ? <Badge variant="outline" className="text-xs text-emerald-700 bg-emerald-50 border-emerald-200">Billable</Badge>
          : <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>;
      },
    },
    {
      id: 'budget',
      header: 'Budget Cost',
      cell: ({ row }) =>
        row.original.totalBudget > 0 ? (
          <SensitiveData>
            <span className="text-sm font-medium">{formatCurrency(row.original.totalBudget)}</span>
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">No budget</span>
        ),
    },
    {
      id: 'actual',
      header: 'Actual Cost',
      cell: ({ row }) => (
        <SensitiveData>
          <span className="text-sm">{formatCurrency(row.original.project.forecastInputs.actualCosts)}</span>
        </SensitiveData>
      ),
    },
    {
      id: 'eac',
      header: 'EAC Cost',
      cell: ({ row }) =>
        row.original.totalBudget > 0 ? (
          <SensitiveData>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium">{formatCurrency(row.original.eac)}</span>
              {globalMethod === 're-estimate' && (
                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 font-mono text-amber-700 bg-amber-50 border-amber-300">RE</Badge>
              )}
            </div>
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">No budget</span>
        ),
    },
    {
      id: 'variance',
      header: 'Forecast Variance £',
      cell: ({ row }) =>
        row.original.totalBudget > 0 ? (
          <SensitiveData>
            <VariancePill value={row.original.forecastVariance} />
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'variancePct',
      header: 'Forecast Variance %',
      cell: ({ row }) =>
        row.original.totalBudget > 0 ? (
          <SensitiveData>
            <VariancePill value={row.original.forecastVariancePct} isPercent />
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'margin',
      header: 'EAC Margin',
      cell: ({ row }) => {
        const { eacMarginPct } = row.original;
        if (eacMarginPct == null) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        const marginClass =
          eacMarginPct < thresholds.marginRedThreshold
            ? 'text-error-600'
            : eacMarginPct < thresholds.marginAmberThreshold
            ? 'text-warning-600'
            : 'text-success-600';
        return (
          <SensitiveData>
            <span className={`text-sm font-medium ${marginClass}`}>{eacMarginPct.toFixed(1)}%</span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'forecastRevenue',
      header: 'Forecast Revenue',
      cell: ({ row }) =>
        row.original.forecastRevenue > 0 ? (
          <SensitiveData>
            <span className="text-sm font-medium">{formatCurrency(row.original.forecastRevenue)}</span>
          </SensitiveData>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'forecastMargin',
      header: 'Forecast Margin £',
      cell: ({ row }) => {
        const { forecastRevenue, forecastMargin } = row.original;
        if (forecastRevenue === 0) return <span className="text-sm text-muted-foreground">—</span>;
        return (
          <SensitiveData>
            <span className={cn('text-sm font-medium', forecastMargin >= 0 ? 'text-success-600' : 'text-error-600')}>
              {forecastMargin >= 0 ? '+' : ''}{formatCurrency(forecastMargin)}
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'forecastMarginPct',
      header: 'Margin %',
      cell: ({ row }) => {
        const { forecastMarginPct } = row.original;
        if (forecastMarginPct == null) return <span className="text-sm text-muted-foreground">—</span>;
        return (
          <SensitiveData>
            <span className={cn('text-sm font-medium', forecastMarginPct >= 0 ? 'text-success-600' : 'text-error-600')}>
              {forecastMarginPct.toFixed(1)}%
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'forecastEnd',
      header: 'Forecast End',
      cell: ({ row }) => {
        const date = row.original.project.forecastEndDate ?? row.original.project.endDate ?? null;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm">{date ? formatDate(date) : '—'}</span>
            {row.original.delayDays && (
              <span className="text-xs text-warning-600 flex items-center gap-0.5">
                <Clock className="h-3 w-3" />
                {row.original.delayDays}d late
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'completion',
      header: 'Completion %',
      cell: ({ row }) => {
        const pct = row.original.project.completionPercent;
        return (
          <div className="w-20 space-y-1">
            <div className="text-xs font-medium">{pct}%</div>
            <Progress value={pct} className="h-1.5" />
          </div>
        );
      },
    },
  ], [thresholds, globalMethod]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 pb-10">
      {/* Back navigation — top-left, above the heading */}
      <BackToProjectLink projectId={initReportProjectId} />

      <PageHeaderBlock
        title="Project Forecast"
        subtitle={
          projectFilter
            ? `Cash flow view — ${projects.find(p => p.projectId === projectFilter)?.projectName ?? 'Selected Project'}`
            : 'Forward-looking cost and revenue projections across all active projects.'
        }
      />

      <Card>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center p-6 pb-4" data-tour="project-forecast-filters">
        <div className="flex-1" />

        {/* Project Filter (drives single-project cash flow view) */}
        <Select
          value={projectFilter ? String(projectFilter) : 'all'}
          onValueChange={v => handleProjectFilterChange(v === 'all' ? null : Number(v))}
        >
          <SelectTrigger className="h-8 w-52 text-sm">
            <SelectValue placeholder="All projects (portfolio)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects (portfolio)</SelectItem>
            {projects.map(p => (
              <SelectItem key={p.projectId} value={String(p.projectId)}>
                <span className="font-mono text-xs text-muted-foreground mr-1.5">{p.projectCode}</span>
                {p.projectName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* PM Filter */}
        <Popover open={pmComboboxOpen} onOpenChange={setPmComboboxOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="h-8 w-44 justify-between text-sm font-normal">
              <span className="truncate">{pmFilter === 'all' ? 'All PMs' : pmFilter}</span>
              <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search project managers..." className="h-8" />
              <CommandList>
                <CommandEmpty>No project managers found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem value="all" onSelect={() => { setPmFilter('all'); setPmComboboxOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 ${pmFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                    All PMs
                  </CommandItem>
                  {pmOptions.map(pm => (
                    <CommandItem key={pm} value={pm} onSelect={() => { setPmFilter(pm); setPmComboboxOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${pmFilter === pm ? 'opacity-100' : 'opacity-0'}`} />
                      {pm}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Billable Filter */}
        <Select value={billableFilter} onValueChange={setBillableFilter}>
          <SelectTrigger className="w-36 h-8 text-sm">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            <SelectItem value="billable">Billable</SelectItem>
            <SelectItem value="non-billable">Non-billable</SelectItem>
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 h-8 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active Projects</SelectItem>
            <SelectItem value="all">All Projects</SelectItem>
            <SelectItem value="2">In Progress</SelectItem>
            <SelectItem value="1">Planning</SelectItem>
            <SelectItem value="3">Completed</SelectItem>
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-sm w-52"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Export to Excel */}
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9"
          onClick={handleExportExcel}
          disabled={isExporting || (summary.totalProjects === 0)}
          title="Export to Excel"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Error State */}
      {fetchError && !loading && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive mb-3" />
          <p className="text-muted-foreground text-sm max-w-md mb-4">{fetchError}</p>
          <Button variant="outline" size="sm" onClick={() => { setFetchError(null); setCurrentPage(1); }}>
            Try Again
          </Button>
        </div>
      )}

      {projectFilter ? (
        /* ── Single-project cash flow view ─────────────────────────────── */
        <SingleProjectCashFlowContent
          projectId={projectFilter}
          projectName={projects.find(p => p.projectId === projectFilter)?.projectName}
          projectCode={projects.find(p => p.projectId === projectFilter)?.projectCode}
        />
      ) : (
        /* ── Portfolio view ─────────────────────────────────────────────── */
        <>
      {/* Summary Cards */}
      <div className="px-6 pb-4">
      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
      ) : fetchError ? null : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <CompactKpiCard
            title="Total EAC Cost"
            value={<SensitiveData>{formatCurrency(summary.totalEac)}</SensitiveData>}
            description={`vs ${formatCurrency(summary.totalBudget)} budget`}
            icon={DollarSign}
            accentVariant="info"
            className="shadow-none"
          />
          <CompactKpiCard
            title="Total Budget Cost"
            value={<SensitiveData>{formatCurrency(summary.totalBudget)}</SensitiveData>}
            description={`across ${summary.totalProjects} projects`}
            icon={Wallet}
            accentVariant="info"
            className="shadow-none"
          />
          <CompactKpiCard
            title="Forecast Variance"
            value={<SensitiveData>{summary.forecastVariance > 0 ? '+' : ''}{formatCurrency(summary.forecastVariance)}</SensitiveData>}
            description={
              summary.totalBudget > 0
                ? `${((summary.forecastVariance / summary.totalBudget) * 100).toFixed(1)}% ${summary.forecastVariance > 0 ? 'over budget' : 'under budget'}`
                : '—'
            }
            icon={summary.forecastVariance > 0 ? TrendingUp : TrendingDown}
            accentVariant={summary.forecastVariance > 0 ? 'error' : 'success'}
            className="shadow-none"
          />
          <CompactKpiCard
            title="At-Risk Projects"
            value={summary.atRisk}
            description={`of ${summary.totalProjects} projects`}
            icon={TriangleAlert}
            accentVariant={summary.atRisk > 0 ? 'error' : 'success'}
            className="shadow-none"
          />
          <CompactKpiCard
            title="Total Forecast Revenue"
            value={<SensitiveData>{formatCurrency(summary.totalForecastRevenue)}</SensitiveData>}
            description="projected final revenue"
            icon={Receipt}
            accentVariant="info"
            className="shadow-none"
          />
          <CompactKpiCard
            title="Total Forecast Margin"
            value={<SensitiveData>{formatCurrency(summary.totalForecastMargin)}</SensitiveData>}
            description={
              summary.totalForecastRevenue > 0
                ? `${((summary.totalForecastMargin / summary.totalForecastRevenue) * 100).toFixed(1)}% margin`
                : '—'
            }
            icon={summary.totalForecastMargin >= 0 ? TrendingUp : TrendingDown}
            accentVariant={summary.totalForecastMargin >= 0 ? 'success' : 'error'}
            className="shadow-none"
          />
        </div>
      )}
      </div>

      {/* Table */}
      <CardContent className="p-0">
      {loading ? (
        <div className="space-y-2 p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : fetchError ? null : enriched.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="h-8 w-8 text-muted-foreground mb-3" />
          {projects.length === 0 ? (
            <p className="text-muted-foreground text-sm max-w-xs">
              No active projects found. Projects with status 'In Progress' or 'Planning' will appear here.
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">No projects match the current filters.</p>
          )}
        </div>
      ) : (
        <DataTableEnhanced
          data-tour="project-forecast-table"
          columns={columns}
          data={enriched}
          columnSorts={columnSorts}
          onColumnSortChange={handleColumnSortChange}
          onRowClick={handleRowClick}
          getRowId={row => String(row.project.projectId)}
          className="rounded-none border-0 border-t cursor-pointer"
          paginationContent={
            pagination && pagination.totalItems > 0 ? (
              <PaginationControls
                pagination={pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            ) : undefined
          }
        />
      )}
      </CardContent>
        </>
      )}
      </Card>

      {/* Side Sheet — portfolio view only */}
      {!projectFilter && selectedRow && (
        <ForecastSideSheet
          row={selectedRow}
          onClose={() => setSelectedProjectId(null)}
        />
      )}
    </div>
  );
}
